import { useNavigate, useParams, useSearchParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import { formatString } from '@common/utils/formatString';
import {
    nzaServiceDeclarationActivityAddSchema,
    nzaServiceDeclarationActivityEditSchema,
} from '@common/validation/nzaServiceDeclarationActivity';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import type { IServiceDeclarationActivity } from '../../interfaces/declarationActivity';
import {
    CancelButton,
    DangerAlertWhenErrorCode,
    FormContainer,
    Header,
    InputContainer,
    InputCurrency,
    Loading,
    SaveSubmit,
} from '../../elements/common';
import { FormContextProvider } from '../../elements/formContext';
import { FormLabel } from '../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../elements/forms/HorizontalFormGroup';
import { DateField } from '../../fields/Date';
import { DropdownField } from '../../fields/Dropdown';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';

interface INzaServiceDeclarationActivity {
    endDate: string | null;
    id?: string;
    nzaServiceDeclarationRuleId?: string;
    serviceDeclarationActivityId: string;
    startDate: string;
    supplementPriceActivity: number | null;
}

export function NzaServiceDeclarationActivityAddEdit() {
    const language = getSelectedLanguage();

    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const serviceId = searchParams.getOptional('serviceId');
    const nzaServiceDeclarationRuleId = searchParams.getOptional('nzaServiceDeclarationRuleId');

    const [nzaServiceDeclarationActivity, setNzaServiceDeclarationActivity] = useState<INzaServiceDeclarationActivity>({
        endDate: null,
        serviceDeclarationActivityId: '',
        startDate: '',
        supplementPriceActivity: null,
    });

    const [serviceDeclarationActivities, setServiceDeclarationActivities] = useState<IServiceDeclarationActivity[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState(emptyValidationErrors);
    const [errorCode, setErrorCode] = useState<string>();

    assertHasAuthorization(ManagerAuthorization.servicesRead);
    assertHasAuthorization(ManagerAuthorization.serviceDeclarationRules);

    useEffect(() => {
        if (id) {
            load();
        } else {
            setNzaServiceDeclarationActivity({
                ...nzaServiceDeclarationActivity,
                nzaServiceDeclarationRuleId: nzaServiceDeclarationRuleId,
            });
        }
        loadServiceDeclarationActivities();
    }, []);

    return (
        <FixedWidthTemplate>
            <Header
                content={
                    id
                        ? formatString(language.common.editItem, [language.serviceDeclarationRules.declarationActivity])
                        : formatString(language.common.addItem, [language.serviceDeclarationRules.declarationActivity])
                }
            />
            <DangerAlertWhenErrorCode errorCode={errorCode} />
            <Loading
                item={nzaServiceDeclarationActivity}
                loaded={(loadedNzaServiceDeclarationActivity) => (
                    <FormContainer onSubmit={async () => await save()}>
                        <FormContextProvider
                            value={{
                                errors: errors,
                                item: loadedNzaServiceDeclarationActivity,
                                onChange: async (key, value) => {
                                    await handleChange(key, value);
                                    await validateOne(key, value);
                                },
                                onDoValidate: async () => {
                                    // done in onchange to make sure correct value is validated
                                },
                                placeholderGroupName: 'insurers',
                            }}
                        >
                            {serviceDeclarationActivities && (
                                <DropdownField
                                    fieldKey="serviceDeclarationActivityId"
                                    isRequired
                                    label={language.serviceDeclarationRules.declarationActivity}
                                    placeholder={formatString(language.common.selectItem, [
                                        language.serviceDeclarationRules.declarationActivity,
                                    ])}
                                    selectOptions={serviceDeclarationActivities.map((activity) => ({
                                        text: `${activity.codeActiviteit} - ${activity.activityType} `,
                                        value: activity.id,
                                    }))}
                                />
                            )}

                            <DateField
                                birthDate
                                fieldKey="startDate"
                                isRequired
                                label={language.common.startDate}
                                placeholder={language.common.startDate}
                                size="medium"
                            />
                            <DateField
                                birthDate
                                fieldKey="endDate"
                                label={language.common.endDate}
                                placeholder={language.common.endDate}
                                size="medium"
                            />

                            <HorizontalFormGroup hasErrors={errors.has('supplementPriceActivity')}>
                                <FormLabel>{language.serviceDeclarationRules.supplementPriceActivity}</FormLabel>
                                <InputContainer medium>
                                    <InputCurrency
                                        error={errors.get('supplementPriceActivity')}
                                        fieldKey="supplementPriceActivity"
                                        value={loadedNzaServiceDeclarationActivity.supplementPriceActivity}
                                    />
                                </InputContainer>
                            </HorizontalFormGroup>
                        </FormContextProvider>

                        <CancelButton
                            disabled={isSaving}
                            to={getReturnUrl()}
                        />
                        <SaveSubmit disabled={isSaving} />
                    </FormContainer>
                )}
            />
        </FixedWidthTemplate>
    );

    async function handleChange<K extends keyof INzaServiceDeclarationActivity>(
        name: K,
        value: INzaServiceDeclarationActivity[K]
    ) {
        setNzaServiceDeclarationActivity({ ...nzaServiceDeclarationActivity, [name]: value });
    }

    async function validateOne<K extends keyof INzaServiceDeclarationActivity>(
        name: K,
        value: INzaServiceDeclarationActivity[K]
    ) {
        setErrors(
            updateValidationErrorsForOne(
                nzaServiceDeclarationActivityEditSchema,
                { ...nzaServiceDeclarationActivity, [name]: value },
                errors,
                name
            )
        );
    }

    async function load() {
        const url = `/api/nzaServiceDeclarationActivities/${id}`;
        const result = await fetchJson(url, 'GET');

        setNzaServiceDeclarationActivity(result.json);
    }

    async function save() {
        let url = '/api/nzaServiceDeclarationActivities/';

        if (id) {
            url += id;
        }

        const { errors: validatedErrors, item } = validate(
            id ? nzaServiceDeclarationActivityEditSchema : nzaServiceDeclarationActivityAddSchema,
            nzaServiceDeclarationActivity
        );
        setErrors(validatedErrors);
        if (validatedErrors.size > 0) {
            return;
        }

        setIsSaving(true);

        const result = await fetchJson(url, 'POST', item);
        if (result.errorCode) {
            setErrorCode(result.errorCode);
            setIsSaving(false);
        } else {
            navigate(getReturnUrl());
        }
    }

    function getReturnUrl() {
        return `/services/${serviceId}?tab=nzaServiceDeclarationRules`;
    }

    async function loadServiceDeclarationActivities() {
        if (serviceId) {
            const url = `/api/serviceDeclarationActivities?serviceId=${serviceId}`;
            const result = await fetchJson(url, 'GET');
            setServiceDeclarationActivities(result.json.items);
        }
    }
}
